import { useEffect, useRef, useState } from "react";

import { ParagonCrmProvider } from "components/integrations/managed_integrations/crmProviders";
import { FieldMappingData, MappableField } from "lib/generated/integration-api";
import { defaultMappingFor } from "lib/integrations/paragonCrm/defaultMapping";
import { acceptsCrmField } from "lib/integrations/paragonCrm/fieldCompatibility";
import { CrmRecordKind, RecordFieldSpec } from "lib/integrations/paragonCrm/recordFields";

type MappingDraft = {
  /** The CRM field each of our own fields goes to, keyed by ours. */
  mapping: Record<string, string>;
  /** Takes a choice as the user's own, so what is on the form stops being a suggestion. */
  chooseMapping: (update: (current: Record<string, string>) => Record<string, string>) => void;
  /** Whether what is on the form was suggested rather than chosen. */
  isSuggested: boolean;
  /** Starts again from nothing, for a section pointed at another object. */
  clear: () => void;
  markSaved: () => void;
};

type Args = {
  provider: ParagonCrmProvider;
  kind: CrmRecordKind;
  /** The CRM object the section is pointed at, which the fields below belong to. */
  entityName: string;
  /** What was configured before, if anything. */
  saved: FieldMappingData | undefined;
  /** The application fields this record and object offer, with their references resolved. */
  specs: RecordFieldSpec[];
  /** What the CRM reports for the object, once it has answered. */
  crmFields: MappableField[] | undefined;
};

/**
 * The mapping a section is editing: what is in it, and whether the customer put it there.
 *
 * One hook because the three answers are the same answer. A suggestion only reaches a section
 * nobody has configured, anything the user touches stops being one, and a field the CRM no longer
 * has belongs to neither — so splitting them would leave each half guessing at the others.
 */
export function useMappingDraft({
  provider,
  kind,
  entityName,
  saved,
  specs,
  crmFields,
}: Args): MappingDraft {
  const [mapping, setMapping] = useState<Record<string, string>>(() => savedMapping(saved));
  const [isSuggested, setIsSuggested] = useState(false);
  /** The object a suggestion has already been made for, so a later edit is never overwritten. */
  const suggestedFor = useRef<string>();

  /**
   * A section nobody has configured is filled in with what usually belongs where, once the CRM has
   * said which of those fields it actually has.
   *
   * Only where nothing was ever saved. A saved mapping with no fields in it is a section somebody
   * deliberately emptied, and filling it back in would undo that on their next visit.
   */
  useEffect(() => {
    if (saved || !crmFields || suggestedFor.current === entityName) return;
    suggestedFor.current = entityName;

    const suggestion = defaultMappingFor(provider, kind, entityName, specs, crmFields);
    if (Object.keys(suggestion).length === 0) return;

    setMapping(suggestion);
    setIsSuggested(true);
  }, [saved, crmFields, provider, kind, entityName, specs]);

  // A mapping saved earlier can name a field the CRM has since dropped, renamed or retyped. Its row
  // shows nothing, so leaving it in state would submit a field the user cannot see or clear.
  useEffect(() => {
    if (!crmFields) return;
    setMapping((current) => {
      const kept: Record<string, string> = {};
      for (const spec of specs) {
        const chosen = current[spec.key];
        const field = crmFields.find((candidate) => candidate.id === chosen);
        if (field && acceptsCrmField(spec, field)) kept[spec.key] = chosen;
      }
      // Nothing dropped: the same object, so this settles rather than re-running on every fetch.
      return Object.keys(kept).length === Object.keys(current).length ? current : kept;
    });
  }, [crmFields, specs]);

  return {
    mapping,
    isSuggested,
    chooseMapping: (update) => {
      setIsSuggested(false);
      setMapping(update);
    },
    clear: () => {
      setMapping({});
      setIsSuggested(false);
    },
    // Saved is saved, whatever it started as. The saved mapping arrives on a refetch, which the
    // notice must not wait for.
    markSaved: () => setIsSuggested(false),
  };
}

function savedMapping(saved: FieldMappingData | undefined): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (const field of saved?.fields ?? []) mapping[field.stotlesField] = field.crmField;
  return mapping;
}
